import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputAdornment from "@mui/material/InputAdornment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import Alert from "components/Alert/Alert"
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "../components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { useDispatch, useSelector } from "react-redux";
import { Login } from "store/slice/usersSlice";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { EmailVerificationAction } from "store/slice/usersSlice";

import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

function Basic() {

  const dispatch = useDispatch()
  const { t } = useTranslation();
  const { search } = useLocation();
  
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get('token');

  const usersStore = useSelector(store => store.user);
  const [rememberMe, setRememberMe] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
      if (token) {
       dispatch(EmailVerificationAction(token))
      }
  }, [token])

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  const handleSubmit = (values) => {

    dispatch(Login(values))
  };

  return (
    <BasicLayout image={bgImage}>
      <Card style={{border: "px oslid red"}}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign in
          </MDTypography>
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
            <MDTypography variant="h5" fontWeight="medium" color="white" mt={1}>
              Live Management Admin
            </MDTypography>
          </Grid>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          {usersStore.error.status ? <Alert  message={t(usersStore.error?.message)}  />: ''}
          {usersStore.emailVerError.status ? <Alert  message={t(usersStore.emailVerError?.message)}  />: ''}
          {usersStore.emailVerSeucces.status ? <Alert  message={t(usersStore.emailVerSeucces?.message)} isSuccess={true} />: ''}
          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange }) => (
              <Form>
                <MDBox mb={2}>
                  <MDInput
                    type="email"
                    label="Email"
                    name="email"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={values.email}
                    onChange={handleChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "rgba(248,249,255,0.7)",
                        transition: "box-shadow 0.2s",
                        "& fieldset": { borderColor: "rgba(99,102,241,0.2)", borderWidth: "1.5px" },
                        "&:hover fieldset": { borderColor: "rgba(99,102,241,0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1.5px" },
                        "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(99,102,241,0.1)" },
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#6366f1" },
                    }}
                    helperText={
                      touched.email && errors.email ? (
                        <span style={{ color: "#ef4444", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                          <ErrorOutlineIcon style={{ fontSize: 13 }} />
                          {errors.email}
                        </span>
                      ) : null
                    }
                    InputProps={{
                      endAdornment: touched.email && errors.email && (
                        <InputAdornment position="end">
                          <ErrorOutlineIcon style={{ color: "#ef4444", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type={showPassword ? "text" : "password"}
                    label="Password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "rgba(248,249,255,0.7)",
                        transition: "box-shadow 0.2s",
                        "& fieldset": { borderColor: "rgba(99,102,241,0.2)", borderWidth: "1.5px" },
                        "&:hover fieldset": { borderColor: "rgba(99,102,241,0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1.5px" },
                        "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(99,102,241,0.1)" },
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#6366f1" },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            size="small"
                            sx={{ color: "rgba(99,102,241,0.55)", "&:hover": { color: "#6366f1" } }}
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mt={4} mb={1}>
                  <MDButton type="submit" variant="gradient" color="info" fullWidth>
                    Sign in
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
          <MDBox mt={3} mb={1} textAlign="center">
            <MDTypography variant="button" color="text">
              Don&apos;t have an account?{" "}
              <MDTypography
                component={Link}
                to="/auth/register"
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
              >
                Sign up
              </MDTypography>
            </MDTypography>
          </MDBox>
          <MDBox mt={1} mb={1} textAlign="center">
            <MDTypography variant="button" color="text">
              Forgot your password?{" "}
              <MDTypography
                component={Link}
                to="/auth/reset-password"
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
              >
                Reset it here
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
